import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { operator as operatorApi, self } from "../lib/api";
import { PermissionsProvider } from "../lib/permissions";
import { Avatar } from "../lib/Avatar";
import { Icon, type IconName } from "../lib/Icon";
import { ImpersonationBanner } from "../lib/ImpersonationBanner";
import { RULogo } from "../lib/Logo";
import { ThemeToggle } from "../lib/ThemeToggle";
import { ConnectBanner } from "../lib/ConnectBanner";
import { TrialBanner } from "../lib/TrialBanner";
import { GateHealthBanner } from "../lib/GateHealthBanner";
import { useCurrentFacility } from "../lib/currentFacility";
import { useIdentify } from "../lib/useIdentify";
import { useLocale } from "../lib/locale";
import { registerCampaign, resetIdentity } from "../lib/telemetry";
import { captureUtmFromLocation } from "../lib/utm";
import { clearAllAuth } from "../lib/auth";
import { cn } from "../lib/cn";
import { tenantLabel, unitLabel } from "../lib/verticals";
import type { FacilityRead } from "../lib/api";

type FacilityType = FacilityRead["facility_type"];

function buildNav(
  ft: FacilityType,
): Array<{ to: string; label: string; icon: IconName; badge?: string }> {
  const isMultifamily = ft === "multifamily";
  return [
    { to: "/app/today", label: "Today", icon: "home" },
    { to: "/app/portfolio", label: "Portfolio", icon: "chart" },
    { to: "/app/units", label: unitLabel(ft, true), icon: "grid" },
    { to: "/app/tenants", label: tenantLabel(ft, true), icon: "users" },
    // Multifamily-only entries — maintenance ticketing + leasing pipeline.
    // Storage / RV / parking operators don't use them; we hide rather than
    // show empty pages to keep the chrome quiet for non-multifamily.
    ...(isMultifamily
      ? [
          { to: "/app/maintenance", label: "Maintenance", icon: "key" as IconName },
          { to: "/app/leasing", label: "Leasing", icon: "tag" as IconName },
          { to: "/app/tours", label: "Tours", icon: "users" as IconName },
        ]
      : []),
    // Leads is the storage/RV/parking analog — for multifamily Leasing
    // replaces it, so we don't double up.
    ...(isMultifamily ? [] : [{ to: "/app/leads", label: "Leads", icon: "tag" as IconName }]),
    { to: "/app/pricing", label: "Pricing", icon: "tag" },
    { to: "/app/billing", label: "Billing", icon: "wallet" },
    // Gate is the storage / RV / parking access-control surface (PTI
    // Falcon, SecurGuard, INSOMNIAC keypad provisioning). Multifamily
    // building access lives on a different vendor stack (fobs, mobile
    // keys, intercoms) that doesn't ship in NestPlot yet, so hide the
    // tab entirely for multifamily — leaving it there only points the
    // operator at an empty event log + provider-not-configured banner.
    ...(ft === "multifamily"
      ? []
      : [{ to: "/app/gate", label: "Gate", icon: "gate" as IconName }]),
    { to: "/app/reports", label: "Reports", icon: "chart" },
    { to: "/app/leases", label: "Leases", icon: "doc" },
    // Lien sales is a storage-specific legal flow — hide for non-storage.
    ...(ft === "self_storage" || ft === "cold_storage" || ft === "mixed" || ft === undefined
      ? [{ to: "/app/lien-sales", label: "Lien sales", icon: "lock" as IconName }]
      : []),
    { to: "/app/settings/facility", label: "Settings", icon: "menu" },
  ];
}

function buildMobileTabs(
  ft: FacilityType,
): Array<{ to: string; label: string; icon: IconName }> {
  return [
    { to: "/app/today", label: "Today", icon: "home" },
    { to: "/app/units", label: unitLabel(ft, true), icon: "grid" },
    { to: "/app/tenants", label: tenantLabel(ft, true), icon: "users" },
    { to: "/app/billing", label: "Billing", icon: "wallet" },
    { to: "/app/settings/facility", label: "More", icon: "menu" },
  ];
}

export function OperatorShell() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // After Stripe Checkout the operator lands back here with
  // ?checkout=success — invalidate the cached subscription so the
  // TrialBanner re-fetches the now-`active` status. Clean the URL so
  // a refresh doesn't keep re-firing the toast.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const checkout = params.get("checkout");
    if (!checkout) return;
    if (checkout === "success") {
      qc.invalidateQueries({ queryKey: ["subscriptions", "me"] });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
      toast.success("You're all set — plan is active.");
    } else if (checkout === "cancel") {
      toast.info("Checkout canceled — your trial keeps running.");
    }
    params.delete("checkout");
    const query = params.toString();
    navigate(
      { pathname: location.pathname, search: query ? `?${query}` : "" },
      { replace: true },
    );
  }, [location.search, location.pathname, navigate, qc]);
  // Carry UTM attribution onto the app origin: decode the `?_utm=` handoff blob
  // (localStorage is per-origin, so nestplot.com's capture doesn't reach here)
  // and register it with PostHog before useIdentify() binds the operator — so
  // the converted operator's person carries the campaign that brought them.
  useEffect(() => {
    registerCampaign(captureUtmFromLocation());
  }, [location.search]);
  // /auth/me lets the side-nav show the "Admin" entry only for admins.
  const me = useQuery({
    queryKey: ["auth", "me"],
    queryFn: () => self.me(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const isAdmin = me.data?.role === "admin" || me.data?.role === "super_admin";
  // Bind the logged-in operator to PostHog replay + Sentry so sessions
  // show who they are (email) instead of an anonymous distinct_id.
  useIdentify(me.data);
  // Apply the operator's account default UI language once /auth/me lands — but
  // only if this browser has no explicit choice (applyAccountDefaults checks
  // storedLocale). A staff seat at a Montréal operator opens in French; anyone
  // can still override for their own browser.
  const { applyAccountDefaults } = useLocale();
  useEffect(() => {
    if (me.data?.locale) applyAccountDefaults(me.data.locale);
  }, [me.data?.locale, applyAccountDefaults]);
  const facility = useQuery({
    queryKey: ["facilities", "current"],
    queryFn: async () => (await operatorApi.facilities())[0] ?? null,
    staleTime: 5 * 60_000,
    retry: false,
  });
  // Multi-facility — operators on the Small/Multi plan have a chip
  // selector. Single-facility operators see exactly the same control;
  // it just opens a one-item menu.
  const { facilities: allFacilities, current: currentFacility, setCurrent } = useCurrentFacility();
  const [facilityMenuOpen, setFacilityMenuOpen] = useState(false);
  const facilityMenuRef = useRef<HTMLDivElement>(null);
  // Per-vertical nav labels: "Units" → "Apartments" / "Bays" / "Spots"
  // when the current facility isn't self-storage. Operator with a
  // mixed portfolio sees labels for whichever facility is currently
  // selected in the facility-switcher chip.
  const nav = buildNav(currentFacility?.facility_type);
  const mobileTabs = buildMobileTabs(currentFacility?.facility_type);
  const initials = (me.data?.name ?? "—")
    .split(" ")
    .map((p) => p[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase() || "—";

  // Click-outside closes the open popover.
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false);
      }
      if (facilityMenuRef.current && !facilityMenuRef.current.contains(e.target as Node)) {
        setFacilityMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  function signOut() {
    // Use clearAllAuth so both the access token AND the refresh token
    // are dropped. Just clearing the access token left the refresh
    // behind; isOperatorAuthenticated then treated the user as still
    // signed in and the OperatorAuthGate let them right back through.
    clearAllAuth();
    // Drop the PostHog/Sentry identity so the next user on this browser
    // doesn't inherit this operator's replay identity.
    resetIdentity();
    // Soft cache wipe so React Query doesn't hand the next user the
    // previous operator's data on the way to /signin.
    try {
      window.localStorage.removeItem("storops.current-facility-slug");
    } catch {
      /* ignore */
    }
    window.location.href = "/signin";
  }
  return (
    <PermissionsProvider>
    <div className="min-h-screen flex flex-col bg-bg text-ink">
      <ImpersonationBanner />
      <ConnectBanner />
      <TrialBanner />
      <GateHealthBanner />
      <header className="flex items-center justify-between px-4 lg:px-6 py-3 bg-surface border-b border-line">
        <div className="flex items-center gap-3 lg:gap-4 min-w-0">
          <RULogo size={28} wordSize={20} />
          <span className="hidden lg:inline-block w-px h-[22px] bg-line" />
          <div ref={facilityMenuRef} className="relative hidden lg:block">
            <button
              type="button"
              onClick={() => setFacilityMenuOpen((o) => !o)}
              className="inline-flex items-center gap-2 bg-paper border border-line rounded-md px-3 py-[6px] text-[13px] font-medium hover:border-line-strong"
              title="Switch facility"
            >
              <span className="w-[6px] h-[6px] rounded-full bg-ok" />
              <span>{currentFacility?.name ?? facility.data?.name ?? "—"}</span>
              {currentFacility?.city && currentFacility?.state && (
                <span className="text-sub font-normal">
                  · {currentFacility.city}, {currentFacility.state}
                </span>
              )}
              <Icon name="chevdown" size={14} className="text-sub" />
            </button>
            {facilityMenuOpen && (
              <div className="absolute top-full left-0 mt-1 min-w-[280px] bg-paper border border-line-strong rounded-md shadow-lg z-30">
                {allFacilities.map((f) => (
                  <button
                    key={f.slug}
                    type="button"
                    onClick={() => {
                      setCurrent(f.slug);
                      setFacilityMenuOpen(false);
                    }}
                    className={
                      "w-full flex items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-surface " +
                      (currentFacility?.slug === f.slug ? "text-ink font-semibold" : "text-sub")
                    }
                  >
                    <span className="w-[6px] h-[6px] rounded-full bg-ok shrink-0" />
                    <span className="flex-1">{f.name}</span>
                    <span className="text-[11px] text-sub font-mono">{f.state}</span>
                  </button>
                ))}
                <div className="border-t border-line">
                  <Link
                    to="/app/portfolio"
                    onClick={() => setFacilityMenuOpen(false)}
                    className="block px-3 py-2 text-[13px] text-sub hover:text-ink hover:bg-surface"
                  >
                    + Add another facility
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setSearchOpen(true)}
            className="hidden md:flex items-center gap-2 bg-paper border border-line rounded-md px-3 py-[6px] text-[13px] text-sub font-mono w-[260px] hover:border-line-strong"
          >
            <Icon name="search" size={14} />
            <span className="truncate">Search tenants, units, leads…</span>
            <span className="ml-auto border border-line rounded-sm px-1 text-[10px]">⌘K</span>
          </button>
          <ThemeToggle />
          <div ref={notifRef} className="relative">
            <button
              type="button"
              onClick={() => setNotifOpen((v) => !v)}
              className="bg-paper border border-line rounded-md w-9 h-9 grid place-items-center text-ink relative hover:bg-surface"
              aria-label="Notifications"
              aria-expanded={notifOpen}
            >
              <Icon name="bell" size={16} />
              <span className="absolute top-[6px] right-[6px] w-[7px] h-[7px] rounded-full bg-accent" />
            </button>
            {notifOpen && (
              <div className="absolute right-0 top-11 z-30 w-[320px] bg-surface border border-line rounded-md shadow-rollup overflow-hidden">
                <div className="px-4 py-3 border-b border-line font-display font-extrabold text-[14px] uppercase tracking-[-0.005em]">
                  Notifications
                </div>
                <div className="px-4 py-6 text-sm text-sub text-center">
                  You're all caught up.
                  <div className="mt-2">
                    <Link
                      to="/app/today"
                      onClick={() => setNotifOpen(false)}
                      className="text-accent font-semibold"
                    >
                      See today's queue
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div ref={userMenuRef} className="relative">
            <button
              type="button"
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-label="Account menu"
              aria-expanded={userMenuOpen}
              className="rounded-md"
            >
              <Avatar
                initials={initials}
                color="var(--accent)"
                textColor="var(--on-accent)"
                size={36}
                square
              />
            </button>
            {userMenuOpen && (
              <div className="absolute right-0 top-11 z-30 w-[240px] bg-surface border border-line rounded-md shadow-rollup overflow-hidden">
                <div className="px-4 py-3 border-b border-line">
                  <div className="font-semibold text-sm truncate">
                    {me.data?.name ?? "—"}
                  </div>
                  <div className="text-xs text-sub truncate">{me.data?.email ?? ""}</div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/app/settings/facility");
                  }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-paper"
                >
                  Settings
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/app/settings/billing");
                  }}
                  className="w-full text-left px-4 py-2 text-sm hover:bg-paper"
                >
                  Billing
                </button>
                <button
                  type="button"
                  onClick={signOut}
                  className="w-full text-left px-4 py-2 text-sm border-t border-line text-alert hover:bg-paper"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        <aside className="hidden lg:flex w-[200px] flex-col gap-[2px] border-r border-line bg-surface py-4 px-3 shrink-0">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-[10px] px-[10px] py-2 rounded-md text-[13.5px] transition-colors",
                  isActive ||
                    (n.to !== "/app/today" && location.pathname.startsWith(n.to))
                    ? "bg-accent text-on-accent font-bold"
                    : "text-ink font-medium hover:bg-paper",
                )
              }
              end={n.to === "/app/today"}
            >
              {({ isActive }) => (
                <>
                  <Icon name={n.icon} size={16} />
                  <span className="flex-1">{n.label}</span>
                  {n.badge && (
                    <span
                      className={cn(
                        "font-mono text-[10px] font-bold px-[6px] py-[1px] rounded-full",
                        isActive ? "bg-ink text-bg" : "bg-accent-2-soft text-warn",
                      )}
                    >
                      {n.badge}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
          {isAdmin && (
            <NavLink
              to="/app/admin"
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-[10px] px-[10px] py-2 rounded-md text-[13.5px] transition-colors mt-2 border-t border-line pt-3",
                  isActive || location.pathname.startsWith("/app/admin")
                    ? "bg-ink text-bg font-bold"
                    : "text-ink font-medium hover:bg-paper",
                )
              }
            >
              <Icon name="lock" size={16} />
              <span className="flex-1">Admin</span>
              <span className="font-mono text-[9px] tracking-[0.1em] uppercase opacity-70">
                {me.data?.role === "super_admin" ? "super" : "•"}
              </span>
            </NavLink>
          )}
          <div className="mt-auto pt-3 px-[10px] border-t border-line text-[11.5px] text-sub font-mono">
            <div>NestPlot v1.4 · synced 2m</div>
            <div className="inline-flex items-center gap-1 mt-1">
              <span className="w-[6px] h-[6px] rounded-full bg-ok" /> All gates online
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0 overflow-x-hidden pb-[88px] lg:pb-0">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-surface border-t border-line flex justify-around px-4 pt-[10px] pb-[24px] z-20">
        {mobileTabs.map((t) => {
          const active =
            t.to === "/app/today"
              ? location.pathname === "/app/today" || location.pathname === "/app"
              : location.pathname.startsWith(t.to);
          return (
            <NavLink
              key={t.to}
              to={t.to}
              className={cn(
                "flex flex-col items-center gap-[3px]",
                active ? "text-accent" : "text-sub",
              )}
            >
              <Icon name={t.icon} size={20} />
              <span
                className={cn(
                  "text-[10px] font-mono uppercase tracking-[0.04em]",
                  active ? "font-bold" : "font-medium",
                )}
              >
                {t.label}
              </span>
            </NavLink>
          );
        })}
      </nav>

      {searchOpen && (
        <SearchOverlay onClose={() => setSearchOpen(false)} />
      )}
    </div>
    </PermissionsProvider>
  );
}

function SearchOverlay({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-ink/40 z-30 flex items-start justify-center pt-[80px]"
      onClick={onClose}
    >
      <div
        className="bg-paper rounded-xl shadow-rollup border border-line w-[560px] max-w-[90vw] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-line">
          <Icon name="search" size={16} className="text-sub" />
          <input
            autoFocus
            placeholder="Search tenants, units, leads…"
            className="flex-1 bg-transparent outline-none text-sm text-ink placeholder:text-subtle"
          />
          <button onClick={onClose} aria-label="Close search">
            <Icon name="close" size={16} className="text-sub" />
          </button>
        </div>
        <div className="px-4 py-8 text-center text-sub text-sm">
          Type to search across tenants, units, leads, and payments.
        </div>
      </div>
    </div>
  );
}
